import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import SEO from "@/components/SEO";
import { api } from "@/lib/auth";

const PUBLIC_URL = "https://mmalifeusa.com";

export default function BlogIndex() {
    const [params, setParams] = useSearchParams();
    const category = params.get("category") || "";
    const q = params.get("q") || "";
    const page = parseInt(params.get("page") || "1", 10);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState(q);

    useEffect(() => {
        setLoading(true);
        if (typeof window !== "undefined") window.prerenderReady = false;
        api
            .get("/public/blog/posts", {
                params: {
                    page,
                    category: category || undefined,
                    q: q || undefined,
                },
            })
            .then((r) => setData(r.data))
            .catch(() => setData({ posts: [], total: 0, total_pages: 1, categories: [] }))
            .finally(() => {
                setLoading(false);
                requestAnimationFrame(() => {
                    if (typeof window !== "undefined") window.prerenderReady = true;
                });
            });
    }, [page, category, q]);

    const canonical = useMemo(() => {
        const u = new URL("/blog", PUBLIC_URL);
        if (category) u.searchParams.set("category", category);
        if (q) u.searchParams.set("q", q);
        if (page > 1) u.searchParams.set("page", String(page));
        return u.toString();
    }, [category, q, page]);

    const blogLd = useMemo(
        () => ({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Moving Mountains Advisors — Blog",
            url: `${PUBLIC_URL}/blog`,
            description:
                "Plain-English guides on term life, IUL, annuities, Medicare and more — written for real families.",
            publisher: {
                "@type": "InsuranceAgency",
                name: "Moving Mountains Advisors",
                url: PUBLIC_URL,
                logo: {
                    "@type": "ImageObject",
                    url: `${PUBLIC_URL}/brand/logo.png`,
                },
            },
        }),
        []
    );

    const applyFilter = (next) => {
        const n = new URLSearchParams(params);
        Object.entries(next).forEach(([k, v]) => {
            if (v) n.set(k, v);
            else n.delete(k);
        });
        n.delete("page"); // reset to 1 when filter changes
        setParams(n);
    };

    const onSearch = (e) => {
        e.preventDefault();
        applyFilter({ q: searchInput.trim() || undefined });
    };

    return (
        <div data-testid="blog-index-page" className="bg-mm-bg/40 min-h-screen">
            <SEO
                title="Insurance guides & stories | Moving Mountains Advisors"
                description="Plain-English guides on term life, IUL, annuities, Medicare and more — written for real families, not insurance insiders."
                canonical={canonical}
                ogImage={`${PUBLIC_URL}/brand/og-default.png`}
                jsonLd={blogLd}
            />
            <section className="border-b border-mm-line bg-gradient-to-b from-mm-bg to-white py-16 sm:py-20">
                <div className="mm-container">
                    <div className="mm-eyebrow">Insurance guides &amp; stories</div>
                    <h1 className="mt-3 font-serif text-4xl text-mm-navy sm:text-5xl">
                        Plain-English guides for real families.
                    </h1>
                    <p className="mt-3 max-w-2xl text-base text-mm-muted">
                        Written by Brent De Paris and the Moving Mountains team. No
                        jargon, no hard sell — just the clearest explanation we can
                        give.
                    </p>
                </div>
            </section>

            <section className="mm-container py-8">
                <div className="flex flex-wrap items-center gap-3 border-b border-mm-line pb-5">
                    <form onSubmit={onSearch} className="inline-flex items-center gap-2" data-testid="blog-search-form">
                        <div className="relative">
                            <Search
                                size={14}
                                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-mm-muted"
                            />
                            <input
                                type="search"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Search guides..."
                                aria-label="Search blog"
                                className="rounded-full border border-mm-line bg-white py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-mm-sky min-w-[220px]"
                                data-testid="blog-search-input"
                            />
                        </div>
                        <button
                            type="submit"
                            className="rounded-full bg-mm-navy px-4 py-2 text-sm font-semibold text-white hover:bg-mm-blue"
                            data-testid="blog-search-submit"
                        >
                            Search
                        </button>
                    </form>
                    <div className="inline-flex flex-wrap items-center gap-2">
                        <button
                            onClick={() => applyFilter({ category: undefined })}
                            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                                !category
                                    ? "border-mm-navy bg-mm-navy text-white"
                                    : "border-mm-line bg-white text-mm-ink hover:border-mm-navy/40"
                            }`}
                            data-testid="blog-cat-all"
                        >
                            All
                        </button>
                        {(data?.categories || []).map((c) => (
                            <button
                                key={c}
                                onClick={() => applyFilter({ category: c })}
                                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                                    category === c
                                        ? "border-mm-navy bg-mm-navy text-white"
                                        : "border-mm-line bg-white text-mm-ink hover:border-mm-navy/40"
                                }`}
                                data-testid={`blog-cat-${c.toLowerCase()}`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="py-16 text-center text-sm text-mm-muted">
                        Loading guides...
                    </div>
                ) : data && data.posts.length > 0 ? (
                    <div
                        className="grid gap-6 py-10 sm:grid-cols-2 lg:grid-cols-3"
                        data-testid="blog-grid"
                    >
                        {data.posts.map((p) => (
                            <article
                                key={p.slug}
                                className="group overflow-hidden rounded-2xl border border-mm-line bg-white transition-shadow hover:shadow-card"
                                data-testid={`blog-card-${p.slug}`}
                            >
                                <Link to={`/blog/${p.slug}`} className="block aspect-video overflow-hidden bg-mm-bg">
                                    {p.hero_image_url && (
                                        <img
                                            src={p.hero_image_url}
                                            alt={p.hero_image_alt || p.title}
                                            loading="lazy"
                                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        />
                                    )}
                                </Link>
                                <div className="flex flex-1 flex-col gap-2 p-5">
                                    <span className="text-[0.72rem] uppercase tracking-wider text-mm-blue">
                                        {p.category}
                                    </span>
                                    <h3 className="font-serif text-lg text-mm-navy">
                                        <Link to={`/blog/${p.slug}`} className="hover:text-mm-blue">
                                            {p.title}
                                        </Link>
                                    </h3>
                                    <p className="line-clamp-3 text-sm text-mm-muted">
                                        {p.excerpt}
                                    </p>
                                </div>
                            </article>
                        ))}
                    </div>
                ) : (
                    <div
                        className="my-10 rounded-2xl border border-dashed border-mm-line bg-white p-10 text-center text-sm text-mm-muted"
                        data-testid="blog-empty"
                    >
                        {category || q ? (
                            <>
                                No posts match that filter yet.{" "}
                                <button
                                    onClick={() => {
                                        setSearchInput("");
                                        setParams(new URLSearchParams());
                                    }}
                                    className="font-medium text-mm-blue underline"
                                >
                                    Clear filters
                                </button>{" "}
                                to see everything.
                            </>
                        ) : (
                            "New guides are on the way. Check back soon."
                        )}
                    </div>
                )}

                {data && data.total_pages > 1 && (
                    <nav
                        className="flex items-center justify-center gap-3 py-6"
                        aria-label="Pagination"
                        data-testid="blog-pager"
                    >
                        {page > 1 && (
                            <button
                                onClick={() => applyFilter({ page: String(page - 1) })}
                                className="inline-flex items-center gap-1 rounded-full border border-mm-line bg-white px-4 py-1.5 text-sm hover:border-mm-navy/40"
                                data-testid="blog-pager-prev"
                            >
                                <ChevronLeft size={14} /> Prev
                            </button>
                        )}
                        <span className="text-sm text-mm-muted">
                            Page {page} of {data.total_pages}
                        </span>
                        {page < data.total_pages && (
                            <button
                                onClick={() => applyFilter({ page: String(page + 1) })}
                                className="inline-flex items-center gap-1 rounded-full border border-mm-line bg-white px-4 py-1.5 text-sm hover:border-mm-navy/40"
                                data-testid="blog-pager-next"
                            >
                                Next <ChevronRight size={14} />
                            </button>
                        )}
                    </nav>
                )}
            </section>
        </div>
    );
}
